import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Chip, TablePagination, Button, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Visibility, Download } from '@mui/icons-material';
import { registrationAPI, formsAPI } from '../../services/api';
import dayjs from 'dayjs';

function parseSchema(f) {
  if (!f) return { fields: [] };
  let s = typeof f.schema === 'string' ? JSON.parse(f.schema) : (f.schema || {});
  if (typeof s === 'string') { try { s = JSON.parse(s); } catch (e) { s = {}; } }
  let fields = [];
  if (s.fields) fields = s.fields;
  else if (s.sections) { s.sections.forEach(function (sec) { (sec.fields || []).forEach(function (fl) { fields.push(fl); }); }); }
  return { fields: fields };
}

function valToStr(v) {
  if (v == null) return '';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

function respObj(reg) {
  let resp = reg.responses || {};
  if (typeof resp === 'string') { try { resp = JSON.parse(resp); } catch (e) { resp = {}; } }
  return resp;
}

export default function RegistrationList() {
  const { id: formId } = useParams();
  const [regs, setRegs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [fields, setFields] = useState([]);
  const [view, setView] = useState(null);

  useEffect(() => {
    registrationAPI.list(formId, { page: page + 1, per_page: 20 })
      .then((r) => { setRegs(r.data.data); setTotal(r.data.total); });
  }, [formId, page]);

  useEffect(() => {
    formsAPI.get(formId).then((r) => { setFields(parseSchema(r.data.data).fields); });
  }, [formId]);

  const downloadCSV = async () => {
    const r = await registrationAPI.list(formId, { page: 1, per_page: 1000 });
    const all = r.data.data || [];
    const headers = ['Name', 'Email', 'Type', 'Registered'].concat(fields.map((f) => f.label));
    const esc = (s) => '"' + String(s == null ? '' : s).replace(/"/g, '""') + '"';
    const lines = [headers.map(esc).join(',')];
    all.forEach((reg) => {
      const resp = respObj(reg);
      const row = [reg.name, reg.email, reg.participant_type, dayjs(reg.registered_at).format('YYYY-MM-DD HH:mm')]
        .concat(fields.map((f) => valToStr(resp[f.id])));
      lines.push(row.map(esc).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'registrations.csv'; a.click();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Registrations</Typography>
        <Button startIcon={<Download />} variant="outlined" onClick={downloadCSV}>Download CSV</Button>
      </Box>
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {regs.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.email}</TableCell>
                  <TableCell>
                    <Chip label={r.participant_type === 'scholar' ? 'Scholar' : 'Self-funded'} size="small"
                      color={r.participant_type === 'scholar' ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell>{dayjs(r.registered_at).format('MMM D, YYYY HH:mm')}</TableCell>
                  <TableCell>
                    <Button size="small" startIcon={<Visibility />} onClick={() => setView({ reg: r, resp: respObj(r) })}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
              {regs.length === 0 && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No registrations yet
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)}
          rowsPerPage={20} rowsPerPageOptions={[20]} />
      </Card>

      <Dialog open={!!view} onClose={() => setView(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Registration details</DialogTitle>
        <DialogContent dividers>
          {view && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}><b>Name:</b> {view.reg.name}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}><b>Email:</b> {view.reg.email}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}><b>Type:</b> {view.reg.participant_type === 'scholar' ? 'Scholar' : 'Self-funded'}</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}><b>Registered:</b> {dayjs(view.reg.registered_at).format('MMM D, YYYY HH:mm')}</Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {fields.map((f) => (
                      <TableRow key={f.id}>
                        <TableCell sx={{ color: 'text.secondary', width: '40%', verticalAlign: 'top', wordBreak: 'break-word' }}>{f.label}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-word' }}>{valToStr(view.resp[f.id]) || '—'}</TableCell>
                      </TableRow>
                    ))}
                    {fields.length === 0 && (
                      <TableRow><TableCell sx={{ color: 'text.secondary' }}>No response fields</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setView(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
